import { useContext, useEffect, useRef, useState } from 'react';

import {
  FiberManualRecord as CircleIcon,
  Send,
  Check,
  Close
} from '@mui/icons-material';
import {
  Box,
  Stack,
  Typography,
  Divider,
  TextField,
  Paper
} from '@mui/material';

import { useQueryClient } from '@tanstack/react-query';

import Button from '@/components/button';
import Card from '@/components/card';
import { ViolationContext } from '@/contexts/ViolationContext';
import { useAddViolationComment } from '@/hooks/queries/violations';
import { useAppSelector } from '@/hooks/redux';
import useAlert from '@/hooks/useAlert';
import type { Violations } from '@/models/violations';
import { getPrettyDateWithTime } from '@/utils/datesAndTime/datesAndTime';

const TimelineIcon = ({ type, content }: { type: string; content: string }) => {
  const getIconProps = () => {
    const lowerType = type.toLowerCase();
    const lowerContent = content.toLowerCase();

    const iconMap: Record<string, { component: any; color: string }> = {
      opinion:
        lowerContent.includes('true') || lowerContent.includes('agree')
          ? { component: Check, color: 'success.main' }
          : { component: Close, color: 'error.main' },
      rating: (() => {
        if (lowerContent.includes('violation')) {
          return { component: CircleIcon, color: 'error.main' };
        }
        if (lowerContent.includes('questionable')) {
          return { component: CircleIcon, color: 'warning.main' };
        }
        if (lowerContent.includes('compliant')) {
          return { component: CircleIcon, color: 'success.main' };
        }
        return { component: CircleIcon, color: 'primary.main' };
      })(),
      status: (() => {
        if (lowerContent.includes('closed')) {
          return { component: CircleIcon, color: 'success.main' };
        }
        if (lowerContent.includes('open')) {
          return { component: CircleIcon, color: 'warning.main' };
        }
        if (lowerContent.includes('other')) {
          return { component: CircleIcon, color: 'grey.500' };
        }
        return { component: CircleIcon, color: 'primary.main' };
      })(),
      contact: { component: Send, color: 'primary.main' },
      default: { component: CircleIcon, color: 'primary.main' }
    };

    return iconMap[lowerType] || iconMap.default;
  };

  const { component: IconComponent, color } = getIconProps();

  return <IconComponent sx={{ color, fontSize: 20 }} />;
};

const TimelineItem = ({
  timeline,
  evaluationName,
  isLast
}: {
  timeline: Violations.Timeline;
  evaluationName: string;
  isLast: boolean;
}) => {
  const timelineType = timeline.type.toLowerCase();
  const timelineContent = timeline.content.toLowerCase();

  const getDisplayContent = () => {
    switch (timelineType) {
      case 'comment':
        return timeline.content;

      case 'opinion':
        return timelineContent.includes('true')
          ? 'Agreed with the rating'
          : 'Disagreed with the rating';

      case 'rating':
        return timelineContent
          ? `Rated as ${capitalize(timelineContent)} in ${evaluationName}`
          : 'Rated the post';

      case 'status':
        return `Changed the status to ${capitalize(timelineContent)}`;

      case 'contact':
        return `Contacted on ${getPrettyDateWithTime(timelineContent)}`;

      default:
        return timelineContent;
    }
  };

  const capitalize = (text: string) =>
    text.charAt(0).toUpperCase() + text.slice(1);

  // Enhanced rendering for comments that's more aligned with the general design
  if (timelineType === 'comment') {
    // Generate a consistent color based on the author's name
    const getAuthorColor = (name: string) => {
      // List of distinct colors that work well together
      const colors = [
        'primary.main',
        'secondary.main',
        'success.main',
        'info.main',
        'warning.main',
        '#9c27b0', // purple
        '#009688', // teal
        '#795548', // brown
        '#607d8b' // blue grey
      ];

      // Simple hash function to get a consistent color for each name
      const hash = name
        .split('')
        .reduce((acc, char) => acc + char.charCodeAt(0), 0);
      return colors[hash % colors.length];
    };

    const authorColor = getAuthorColor(timeline.author);

    return (
      <Box>
        <Box sx={{ position: 'relative', pl: 2 }}>
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 4,
              bgcolor: authorColor,
              borderRadius: 1
            }}
          />

          <Box sx={{ flexGrow: 1 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ color: authorColor }}
              >
                {timeline.author}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {getPrettyDateWithTime(timeline.createdAt)}
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                pl: 0.5,
                pb: 0.5
              }}
            >
              {timeline.content}
            </Typography>
          </Box>
        </Box>
        {!isLast && <Divider sx={{ my: 1.5 }} />}
      </Box>
    );
  }

  // Standard rendering for other activity types
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" fontWeight={500}>
          {timeline.author}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {getPrettyDateWithTime(timeline.createdAt)}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <TimelineIcon type={timelineType} content={timeline.content} />
        <Typography variant="body2">{getDisplayContent()}</Typography>
      </Stack>
      {!isLast && <Divider sx={{ my: 1 }} />}
    </Box>
  );
};

const ViolationTimeline = () => {
  const alert = useAlert();
  const queryClient = useQueryClient();
  const user = useAppSelector((state) => state.user.currentUser);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [comment, setComment] = useState('');
  const { violationData, postId, urlParams } = useContext(ViolationContext);

  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      const scrollContainer = scrollContainerRef.current;
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [violationData?.timeline]);

  const { mutate: addComment, isPending } = useAddViolationComment({
    onSuccess: () => {
      queryClient.setQueryData(
        ['violation', postId, urlParams],
        (oldData: Violations.Details) => ({
          ...oldData,
          timeline: [
            ...oldData.timeline,
            {
              type: 'comment',
              author: user.name,
              content: comment,
              createdAt: new Date().toISOString()
            }
          ]
        })
      );
    },
    onError: () => {
      alert.error('Some error occurred while adding the comment');
    },
    onSettled: () => {
      setComment('');
    }
  });

  const handleAddComment = () => {
    addComment({
      postId,
      data: { comment }
    });
  };

  if (violationData?.isExpired) return <></>;
  const commentSubmit = comment.trim() && !isPending;

  return (
    <Card
      title="Timeline"
      isCollapsible
      content={
        <Box>
          <Paper
            elevation={0}
            ref={scrollContainerRef}
            sx={{
              p: 1.5,
              mb: 3,
              border: '1px solid',
              borderColor: 'primary.light',
              maxHeight: '400px',
              scrollbarWidth: 'thin',
              overflowY: 'auto'
            }}
          >
            {violationData?.timeline && violationData.timeline.length > 0 ? (
              violationData.timeline.map((timeline, index, array) => {
                const getEvaluationName = (timeline: Violations.Timeline) => {
                  if (timeline.type !== 'rating') return '';

                  const expectedCompliance =
                    timeline.content === 'organic' ? '' : timeline.content;

                  const ratingIndex = array
                    .filter(
                      (t) =>
                        t.type === 'rating' && t.content === timeline.content
                    )
                    .indexOf(timeline);

                  return (
                    violationData.violations.filter(
                      (v) => v.compliance === expectedCompliance
                    )[ratingIndex]?.evaluationName || ''
                  );
                };

                return (
                  <TimelineItem
                    key={index}
                    timeline={timeline}
                    evaluationName={getEvaluationName(timeline)}
                    isLast={index === array.length - 1}
                  />
                );
              })
            ) : (
              <Typography variant="body2" color="text.secondary">
                No timeline recorded yet.
              </Typography>
            )}
          </Paper>

          <Typography variant="body1" fontWeight={500}>
            Comment
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Share your thoughts or provide additional information regarding the
            violation.
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (commentSubmit) handleAddComment();
              }
            }}
            placeholder="Add your comment here..."
          />
        </Box>
      }
      actions={
        <Button
          size="small"
          loading={isPending}
          disabled={!commentSubmit}
          text="Submit"
          onClick={handleAddComment}
        />
      }
    />
  );
};

export default ViolationTimeline;
